import { yupResolver } from "@hookform/resolvers/yup";
import { LoadingButton } from "@mui/lab";
import { DialogActions, Divider, FormHelperText, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { FieldValues, useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { useRecoilState, useRecoilValue } from "recoil";
import { MerSETALAIService } from "../../../../../Component/Api/merSETA/LAI/MerSETALAIService";
import AutocompleteWithInfo, { IDropDownOptionsWithDescription } from "../../../../../Component/Common/Components/Autocomplete/AutocompleteWithInfo";
import { DataTable } from "../../../../../Component/Common/Components/DataTable/DataTable";
import AppCurrencyInput from "../../../../../Component/Common/Components/FormValidationInputs/AppCurrencyInput";
import { AppDatepicker } from "../../../../../Component/Common/Components/FormValidationInputs/AppDatepicker";
import AppRadioButtons, { IDropDownOptions } from "../../../../../Component/Common/Components/FormValidationInputs/AppRadioButtons";
import AppTextInput from "../../../../../Component/Common/Components/FormValidationInputs/AppTextInput";
import { IntermediateGlobalLoading } from "../../../../../Component/Common/Components/Loading/IntermediateGlobalLoading";
import { MDButton } from "../../../../../Component/Common/Components/MDButton";
import { MerSETADtoIn, SETAPaymentInformationDto } from "../../../../../Component/Model/Dto/WSP/MerSETALAI";
import { BEECatergoryDto } from "../../../../../Component/Model/Dto/WSP/MerSETASDE";
import { getIOptionsArrayWithInfo } from "../../../../../Helpers/DropdownWithDescriptionHelper";
import { getIOptionsArray } from "../../../../../Helpers/arrayHelper";
import { OrganisationAtom } from "../../../../../State/Organisation.Atom";
import { MerSetaLaiValidationSchema } from "./MerSetaLaiValidationSchema";
import { merSETAPaymentInformationTableMapper } from "./MerSetaPaymentInformationTableColumns";
import { InterventionAtom } from "../Intervention.Atom";
import { ConfirmationDialog } from "../../../../../Component/Common/Components/ConfirmationDialog";

export const MerSetaLaiTrainingPage = () => {
    const history = useHistory();
    const [selectedSDI, setSelectedSDI] = useRecoilState(InterventionAtom);
    const { control, handleSubmit, watch, reset, formState: { isSubmitting }, getValues, setValue } = useForm({
        mode: 'all',
        resolver: yupResolver(MerSetaLaiValidationSchema)
    });

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isLoadingForm, setIsLoadingForm] = useState<boolean>(false);
    const [entriesPerPage] = useState<number>(25);
    const [pageNumber] = useState<number>(1);
    const [skillsSetCodes, setSkillsSetCodes] = useState<IDropDownOptionsWithDescription[]>([]);
    const [skillsProgrammeCodes, setSkillsProgrammeCodes] = useState<IDropDownOptionsWithDescription[]>([]);
    const [beeCategoryCodes, setBeeCategoryCodes] = useState<IDropDownOptions[]>([]);
    const [interventionTitles, setInterventionTitles] = useState<IDropDownOptions[]>([]);
    const [fundingCodes, setFundingCodes] = useState<IDropDownOptions[]>([]);
    const [paymentEntries, setPaymentEntries] = useState<SETAPaymentInformationDto[]>([]);
    const [beeCategories, setBeeCategories] = useState<BEECatergoryDto[]>([]);
    const [providerType, setProviderType] = useState<IDropDownOptions[]>([]);
    const [startDate, setStartDate] = useState<Date>();
    const [endDate, setEndDate] = useState<Date>();
    const [showPaymentError, setShowPaymentError] = useState<boolean>(false);
    const [showInvalidPayment, setShowInvalidPayment] = useState<boolean>(false);
    const [showInvalidCostError, setShowInvalidCostError] = useState<boolean>(false);
    const [showCostLengthError, setShowCostLengthError] = useState<boolean>(false);
    const selectedBeeCategory = watch('beeCategoryId');
    const selectedInterventionType = watch('type');
    const peopleAndTrainingOrganisation = useRecoilValue(OrganisationAtom);
    const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
    const [selectedIdToDelete, setSelectetIdToDelete] = useState<number | undefined>(undefined);

    useEffect(() => {
        fetchData();
    }, []);

    const resetErrors = () => {
        setShowPaymentError(false);
        setShowInvalidPayment(false);
        setShowCostLengthError(false);
        setShowInvalidCostError(false);
    }

    const fetchData = async () => {
        setIsLoading(true)
        try {
            const skillSetCodesResultPromise = MerSETALAIService.GetMerSETASkillsSetCodes();
            const skillProgrammeCodesResultPromise = MerSETALAIService.GetMerSETASkillsProgrammeCodes();
            const interventionTitlesResultPromise = MerSETALAIService.GetMerSETAInterventionTitles();
            const fundingCodesResultPromise = MerSETALAIService.GetMerSETAFundingCodes();
            const beeCategoryResultPromise = MerSETALAIService.GetMerSETACategoryInterventions();

            const [skillSetCodesResult, skillProgrammeCodesResult, interventionTitlesResult, fundingCodesResult, beeCategoryResult] = await Promise.all([
                skillSetCodesResultPromise,
                skillProgrammeCodesResultPromise,
                interventionTitlesResultPromise,
                fundingCodesResultPromise,
                beeCategoryResultPromise
            ]);

            const providerTypes: IDropDownOptions[] = [{
                id: 1,
                value: "Private"
            },
            {
                id: 2,
                value: "Public"
            }]

            const skillsSetOptions = getIOptionsArrayWithInfo(
                skillSetCodesResult.data,
                'id',
                'programmeId',
                'description'
            )
            const skillsProgrammeOptions = getIOptionsArrayWithInfo(
                skillProgrammeCodesResult.data,
                'id',
                'programmeId',
                'description'
            )
            const interventionTitleOptions = getIOptionsArray(
                interventionTitlesResult.data,
                'code',
                'description'
            )
            const beeCategoryCodeOptions = getIOptionsArray(
                beeCategoryResult.data,
                'id',
                'category'
            )
            const fundingCodeOptions = getIOptionsArray(
                fundingCodesResult.data,
                'id',
                'description'
            )
            setBeeCategories(beeCategoryResult.data);
            setSkillsSetCodes(skillsSetOptions);
            setSkillsProgrammeCodes(skillsProgrammeOptions);
            setInterventionTitles(interventionTitleOptions);
            setFundingCodes(fundingCodeOptions);
            setBeeCategoryCodes(beeCategoryCodeOptions);
            setProviderType(providerTypes);
        }
        catch (error) {
            console.error(error);
        }
        finally {
            setIsLoading(false);
        }
    }

    const handleAddPaymentEntry = () => {
        resetErrors();
        const paymentDate = getValues("paymentDate");
        let cost = getValues("cost");

        if(cost != null)
        {
            cost = cost.replace(/\s+/g, '').replace(/,/g, '.');
        }

        if (!paymentDate || !cost || isNaN(cost)) {
            setShowPaymentError(true);
            return;
        }

        if(cost <= 0)
        {
            setShowInvalidCostError(true);
            return;
        }

        if (/\.\d{3,}/.test(cost)) {
            setShowInvalidPayment(true);
            return;
        }

        if (/^\d{13,}/.test(cost)) {
            setShowCostLengthError(true);
            return;
        }

        const newPaymentEntry = {
            paymentDate: paymentDate,
            cost: cost,
        };

        setPaymentEntries((prevPaymentEntries) => [...prevPaymentEntries, newPaymentEntry]);
        setValue("paymentDate", null);
        setValue("cost", "");
    };

    const handleDataSubmit = async (data: FieldValues) => {
        if (paymentEntries.length === 0) {
            setShowPaymentError(true);
            return;
        }
        resetErrors();

        let request: MerSETADtoIn = {
            organisationId: +peopleAndTrainingOrganisation.OrganisationId,
            identifier: data.identifier,
            merSetaInterventionTypesLookupId: data.type.id,
            merSetaInterventionTitlesLookupId: data.title.id,
            courseName: data.courseName,
            merSetaBEECategoryId: data.beeCategoryId.id,
            provider: data.provider,
            providerTypeId: data.providerType,
            startDate: data.startDate,
            endDate: data.endDate,
            qualificationCode: data.qualificationCode,
            merSetaSkillsSetCodeLookupId: data.skillsSet != null ? data.skillsSet.id : undefined,
            merSetaSkillsProgrammeCodesLookupId: data.skillsProgramme != null ? data.skillsProgramme.id : undefined,
            merSetaFundingCodeId: data.fundingCode.id,
            merSETAPaymentInformation: paymentEntries
        };

        if (isEditing()) {
            request = {
                ...request,
                id: selectedSDI.id
            }
        }

        try {
            setIsLoading(true);
            if (isEditing()) {
                const result = await MerSETALAIService.UpdateMerSETALAI(selectedSDI.id, request);
                if (result.data) {
                    toast.success("Entry updated successfully");
                    getMersetaRecord();
                }
            }
            else {
                const result = await MerSETALAIService.CreateMerSETALAI(request);
                if (result.data) {
                    toast.success("Entry saved successfully");
                    setSelectedSDI({
                        id: +result.data
                    });
                }
            }
        }
        catch (error) {
            console.error(error)
            toast.error(isEditing() ? "Failed to update entry" : "Failed to create entry");
        }
        finally {
            setShowPaymentError(false);
            setIsLoading(false);
            reset();
        }
    }

    const handleDelete = (id: number) => {
        setShowConfirmationDialog(true);
        setSelectetIdToDelete(id);
    }

    const handleCloseConfirmation = () => {
        setShowConfirmationDialog(false);
        setSelectetIdToDelete(undefined);
    }

    const deleteSelectedEntry = (index: number) => {
        setPaymentEntries((prevPaymentEntries) => {
            const updatedPaymentEntries = [...prevPaymentEntries];
            updatedPaymentEntries.splice(index, 1);
            return updatedPaymentEntries;
        });
        handleCloseConfirmation();
    };

    let interventionTypes = getIOptionsArray(
        beeCategories?.find(category => category.id === selectedBeeCategory?.id)?.interventionTypes ?? [],
        'code',
        'description'
    );

    if ((interventionTypes?.find(x => x.id === selectedInterventionType?.id) === undefined) && getValues('type') !== null) {
        setValue('type', null)
    }

    const isEditing = () => {
        return selectedSDI.id > 0;
    }

    useEffect(() => {
        if (isEditing() && beeCategoryCodes.length > 0) {
            getMersetaRecord();
        }
    }, [selectedSDI.id, beeCategoryCodes]);

    const getMersetaRecord = async () => {
        setIsLoadingForm(true);
        try {
            const result = await MerSETALAIService.GetById(selectedSDI.id);
            reset(result.data);
            const selectedData = beeCategoryCodes.find(x => x.id === result.data.merSetaBEECategoryId);

            setValue('beeCategoryId', selectedData);
            interventionTypes = getIOptionsArray(
                beeCategories?.find(category => category.id === selectedData?.id)?.interventionTypes ?? [],
                'code',
                'description'
            );

            const selectedType = interventionTypes.find(x => x.id === result.data.merSetaInterventionTypesLookupId);
            setValue('type', selectedType);

            const selectedTitle = interventionTitles.find(title => title.id === result.data.merSetaInterventionTitlesLookupId);
            setValue('title', selectedTitle);

            const selectedFundingCode = fundingCodes.find(fundingCodes => fundingCodes.id === result.data.merSetaFundingCodeId);
            setValue('fundingCode', selectedFundingCode);

            const selectedSkillsSet = skillsSetCodes.find(skillsSet => skillsSet.id === result.data.merSetaSkillsSetCodeLookupId);
            setValue('skillsSet', selectedSkillsSet);

            const selectedSkillsProgramme = skillsProgrammeCodes.find(skillsProgramme => skillsProgramme.id === result.data.merSetaSkillsProgrammeCodesLookupId);
            setValue('skillsProgramme', selectedSkillsProgramme);

            setValue('courseName', result.data.courseName);
            setValue('provider', result.data.provider);
            setValue('providerType', result.data.providerTypeId);
            setValue('startDate', result.data.startDate);
            setValue('endDate', result.data.endDate);
            setValue('qualificationCode', result.data.qualificationCode);
            setPaymentEntries(result.data.merSETAPaymentInformation);
        } catch (error) {
            console.error(error);
        } finally {
            setIsLoadingForm(false);
        }
    }

    const itemCount = paymentEntries.length;

    const tableData = merSETAPaymentInformationTableMapper(paymentEntries, (index) => handleDelete(index));

    if (isLoadingForm || isLoading) return (
        <IntermediateGlobalLoading />
    )
    return (
        <>
            <Grid container spacing={3} alignItems="center" onSubmit={handleSubmit(handleDataSubmit)} id='addSdeForm' component={'form'}>
                <Grid item xs={6} sm={6}>
                    <AutocompleteWithInfo
                        control={control}
                        name="beeCategoryId"
                        label="BEE Category *"
                        items={beeCategoryCodes}
                        size={"small"}
                    />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AutocompleteWithInfo
                        control={control}
                        name="type"
                        label="Intervention Type *"
                        items={interventionTypes}
                        disabled={interventionTypes.length === 0}
                        size="small"
                    />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AutocompleteWithInfo
                        control={control}
                        name="title"
                        label="Intervention Title *"
                        items={interventionTitles}
                        size="small"
                    />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AppTextInput
                        label='Course Name *'
                        name='courseName'
                        control={control}
                    />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AppTextInput
                        label='Training Provider'
                        name='provider'
                        control={control}
                    />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <Typography variant="body2">The provider is:</Typography>
                    <AppRadioButtons control={control} row={true} name="providerType" items={providerType!}
                    />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AppDatepicker
                        control={control}
                        name="startDate"
                        label="Start Date *"
                        inputFormat='yyyy/MM/dd'
                        maxDate={endDate}
                        onChange={setStartDate}
                        showCalendarIcon={true}
                    />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AppDatepicker
                        control={control}
                        name="endDate"
                        label="End Date *"
                        inputFormat='yyyy/MM/dd'
                        minDate={startDate}
                        onChange={setEndDate}
                        showCalendarIcon={true}
                    />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AutocompleteWithInfo
                        control={control}
                        name="fundingCode"
                        label="Funding Code *"
                        items={fundingCodes}
                        size="small"
                        disabled={selectedBeeCategory?.id === 7}
                    />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AutocompleteWithInfo
                        control={control}
                        name="skillsSet"
                        label="Skills Set"
                        items={skillsSetCodes}
                        size="small"
                    />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AutocompleteWithInfo
                        control={control}
                        name="skillsProgramme"
                        label="Skills Programme"
                        items={skillsProgrammeCodes}
                        size="small"
                    />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AppTextInput
                        label='Qualification Code'
                        name='qualificationCode'
                        control={control}
                    />
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AppTextInput
                        label='Intervention Identifier *'
                        name='identifier'
                        control={control}
                    />
                </Grid>
                <Grid item xs={12} sm={12}>
                    <Divider variant="middle" />
                    <Typography>Payment Information</Typography>
                    <Typography variant="body2">Capture completed and future payments for this training intervention.</Typography>
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AppDatepicker
                        control={control}
                        name="paymentDate"
                        label="Payment Date *"
                        inputFormat='yyyy/MM/dd'
                        showError={showPaymentError}
                        showCalendarIcon={true}
                    />
                    {showPaymentError &&
                        <FormHelperText sx={{ color: 'red' }}>Payment Date is required</FormHelperText>
                    }
                </Grid>
                <Grid item xs={6} sm={6}>
                    <AppCurrencyInput
                        label='Cost(excl. VAT) *'
                        name='cost'
                        control={control}
                        showError={showPaymentError || showInvalidPayment || showCostLengthError}
                        onlyAllowCurrency
                    />
                    {showPaymentError &&
                        <FormHelperText sx={{ color: 'red' }}>Cost(excl. VAT) is required</FormHelperText>
                    }
                    {showInvalidPayment &&
                        <FormHelperText sx={{ color: 'red' }}>Please enter a valid amount with a maximum of 2 decimal places.</FormHelperText>
                    }
                    {showCostLengthError &&
                        <FormHelperText sx={{ color: 'red' }}>Cost must be less than R1 trillion</FormHelperText>
                    }
                    {showInvalidCostError &&
                        <FormHelperText sx={{ color: 'red' }}>Cost must be more than R0.00</FormHelperText>
                    }
                </Grid>
            </Grid>
            {showPaymentError &&
                <FormHelperText sx={{ color: 'red' }}>At least 1 payment record is required</FormHelperText>
            }
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: '16px' }}>
                <MDButton variant="outlined" color="primary" onClick={handleAddPaymentEntry}>
                    ADD TO TABLE
                </MDButton>
            </div>
            <div style={{ marginTop: '16px' }}>
                <DataTable
                    table={tableData}
                    isLoading={isLoading}
                    entriesPerPage={{ defaultValue: entriesPerPage }}
                    pageNumber={pageNumber}
                    filteredItemsCount={itemCount}
                    totalItemsCount={itemCount}
                    showBottomMargin={false}
                    showTotalEntries={false}
                    canSearch={false}
                    manualSorting={false}
                    showEntriesPerPage={false}
                />
            </div>
            <DialogActions>
                <LoadingButton loading={isSubmitting} variant='contained' form='addSdeForm' type="submit" color='primary'>Save</LoadingButton>
            </DialogActions>
            <ConfirmationDialog
                showDialog={showConfirmationDialog}
                handleAccept={async () => { deleteSelectedEntry(selectedIdToDelete!) }}
                handleCancel={handleCloseConfirmation}
                heading={"Delete Entry"}
                message={"Are you sure you want to delete the selected entry?"}
                isLoading={false}
            />

        </>
    )
}

